import { Link } from "@tanstack/react-router";
import { ArrowLeftRight, BarChart3, Box, History, type LucideIcon, TrendingUp, Wallet, Waves } from "lucide-react";

interface NavItem {
    to: "/pools" | "/pairs" | "/arbitrages" | "/arbitrages/history" | "/stats" | "/utxos" | "/addresses";
    label: string;
    /** Fits a seventh of a 360 px phone. */
    short: string;
    icon: LucideIcon;
}

const NAV: NavItem[] = [
    { to: "/pools", label: "Pools", short: "Pools", icon: Waves },
    { to: "/pairs", label: "Pairs", short: "Pairs", icon: ArrowLeftRight },
    { to: "/arbitrages", label: "Arbitrages", short: "Arbs", icon: TrendingUp },
    { to: "/arbitrages/history", label: "Arbitrages History", short: "History", icon: History },
    { to: "/stats", label: "Stats", short: "Stats", icon: BarChart3 },
    { to: "/utxos", label: "UTXOs", short: "UTXOs", icon: Box },
    { to: "/addresses", label: "Addresses", short: "Address", icon: Wallet },
];

// `/pools` and `/stats` stay active on their children; `/arbitrages` must not light up on its history child.
const exactFor = (to: NavItem["to"]) => to !== "/pools" && to !== "/stats";

// The active look is a `data-status` variant, not `activeProps`: `activeProps` appends its classes
// next to `text-muted-foreground`, and two text colours resolve by stylesheet order, which left the
// open tab as grey as the rest. Each page's code is fetched while the bar is on screen, so a tap
// never waits for it.
const ACTIVE = "data-[status=active]:text-foreground";

export function TopNav() {
    return (
        <nav aria-label="Pages" className="hidden gap-1 md:flex">
            {NAV.map(({ to, label, icon: Icon }) => (
                <Link
                    key={to}
                    to={to}
                    preload="viewport"
                    activeOptions={{ exact: exactFor(to) }}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-[0.9375rem] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground data-[status=active]:bg-accent ${ACTIVE}`}
                >
                    <Icon className="size-4" />
                    {label}
                </Link>
            ))}
        </nav>
    );
}

export function BottomNav() {
    return (
        <nav
            aria-label="Pages"
            className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-7 border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
        >
            {NAV.map(({ to, short, icon: Icon }) => (
                <Link
                    key={to}
                    to={to}
                    preload="viewport"
                    activeOptions={{ exact: exactFor(to) }}
                    className={`flex min-h-14 touch-manipulation flex-col items-center justify-center gap-1 border-t-2 border-transparent text-[0.6875rem] font-medium text-muted-foreground data-[status=active]:border-foreground ${ACTIVE}`}
                >
                    <Icon className="size-5" />
                    {short}
                </Link>
            ))}
        </nav>
    );
}
